import React from "react";
import { useGov } from "@/hooks/governance";
import { Badge, HashCell } from "@/components/primitives";
import { Table } from "@/components/Table";
import {
  GovPageShell, GovCard, GovRow, GovSection, GovError, GovSkeleton, GovEmpty, useGovNav,
} from "@/components/governance/GovShell";

const memberColumns = (navigate) => [
  {
    key: "cold",
    label: "Cold Credential",
    render: (r) => (
      <button
        type="button"
        data-testid={`committee-member-${r.cold_id}`}
        onClick={() => navigate(`/governance/committee/members/${r.cold_id}`)}
        className="text-left text-foreground hover:text-primary underline decoration-dotted decoration-border underline-offset-2 transition-colors mono text-[12px]"
      >
        {(r.cold_id || "").slice(0, 16)}…{(r.cold_id || "").slice(-6)}
      </button>
    ),
  },
  { key: "status", label: "Status", render: (r) => <Badge tone={r.status === "active" ? "success" : r.status === "expired" ? "warn" : "default"}>{r.status}</Badge> },
  { key: "hot", label: "Hot Key", render: (r) => (r.hot_id ? <HashCell value={r.hot_id} len={10} /> : <span className="text-muted-foreground">not authorized</span>) },
  { key: "exp", label: "Expires", align: "right", render: (r) => <span className="text-muted-foreground">{r.expiration_epoch ? `epoch ${r.expiration_epoch}` : "—"}</span> },
];

const historyColumns = [
  { key: "id", label: "Committee", render: (r) => <span className="text-foreground">#{r.committee_id}</span> },
  { key: "quorum", label: "Quorum", render: (r) => <span>{r.quorum ? `${r.quorum.numerator}/${r.quorum.denominator}` : "—"}</span> },
  { key: "count", label: "Members", align: "right", render: (r) => <span className="text-muted-foreground">{r.member_count ?? "—"}</span> },
  { key: "from", label: "From", align: "right", render: (r) => <span className="text-muted-foreground">{r.effective_from_epoch != null ? `epoch ${r.effective_from_epoch}` : "not enacted"}</span> },
  { key: "to", label: "To", align: "right", render: (r) => <span className="text-muted-foreground">{r.current ? "current" : (r.effective_to_epoch ? `epoch ${r.effective_to_epoch}` : "—")}</span> },
  { key: "current", label: "Active", align: "right", render: (r) => (r.current ? <Badge tone="success">yes</Badge> : <Badge>no</Badge>) },
];

export const Committee = ({ meta }) => {
  const navigate = useGovNav();
  const cur = useGov("/governance/committee");
  const hist = useGov("/governance/committee/history");
  const d = cur.data;
  const members = Array.isArray(d?.members) ? d.members : [];
  const history = Array.isArray(hist.data) ? hist.data : [];

  return (
    <GovPageShell crumbs={meta.crumbs} title={meta.title} subtitle={d ? `Committee #${d.committee_id} · epoch ${d.epoch}` : "Constitutional Committee"}>
      {cur.error ? (
        <GovError error={cur.error} onRetry={cur.refetch} />
      ) : cur.loading && !d ? (
        <GovSkeleton lines={6} />
      ) : d ? (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <GovCard><div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mono">Members</div><div className="text-[20px] mono mt-1">{members.length}</div></GovCard>
            <GovCard><div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mono">Quorum</div><div className="text-[20px] mono mt-1">{d.quorum ? `${d.quorum.numerator}/${d.quorum.denominator}` : "—"}</div></GovCard>
            <GovCard><div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mono">No Confidence</div><div className="mt-2">{d.no_confidence ? <Badge tone="danger">yes</Badge> : <Badge tone="success">no</Badge>}</div></GovCard>
            <GovCard><div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mono">Enacted</div><div className="text-[14px] mono mt-2">{d.enacted_by?.enacted_epoch ? `epoch ${d.enacted_by.enacted_epoch}` : "—"}</div></GovCard>
          </div>

          <GovSection title="Current Members">
            <Table columns={memberColumns(navigate)} rows={members} empty="No committee members." />
          </GovSection>

          <GovSection title="Committee History">
            {hist.error ? <GovError error={hist.error} onRetry={hist.refetch} /> : (
              <Table columns={historyColumns} rows={history} loading={hist.loading} empty="No committee history." />
            )}
          </GovSection>
        </>
      ) : <GovEmpty />}
    </GovPageShell>
  );
};
